import React, { useState, useEffect, useRef } from 'react';
import { X, RefreshCw, Terminal, AlertCircle, AlertTriangle, Info, Copy, Check } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getLineType = (line) => {
  const upper = line.toUpperCase();
  if (upper.includes('ERROR') || upper.includes('CRITICAL') || upper.includes('EXCEPTION') || upper.includes('TRACEBACK')) return 'error';
  if (upper.includes('WARNING') || upper.includes('WARN')) return 'warning';
  return 'info';
};

const LineIcon = ({ type }) => {
  if (type === 'error') return <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0 mt-0.5" />;
  if (type === 'warning') return <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0 mt-0.5" />;
  return <Info className="h-3 w-3 text-gray-500 flex-shrink-0 mt-0.5" />;
};

export const SystemLogsModal = ({ isOpen, onClose }) => {
  const [lines, setLines] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [copied, setCopied] = useState(false);
  const bottomRef = useRef(null);

  const copyLogs = () => {
    const toShow = lines.filter(line => {
      if (filter === 'errors') return getLineType(line) === 'error';
      if (filter === 'warnings') return getLineType(line) === 'warning';
      return true;
    });
    navigator.clipboard.writeText(toShow.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/superadmin/system-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLines(res.data.lines || []);
      setLastRefresh(new Date().toLocaleTimeString('de-DE'));
    } catch {
      setLines(['Fehler beim Laden der Logs.']);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchLogs();
  }, [isOpen]);

  useEffect(() => {
    if (filter === 'all' && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lines, filter]);

  if (!isOpen) return null;

  const filtered = lines.filter(line => {
    if (filter === 'errors') return getLineType(line) === 'error';
    if (filter === 'warnings') return getLineType(line) === 'warning';
    return true;
  });

  const errorCount = lines.filter(l => getLineType(l) === 'error').length;
  const warnCount = lines.filter(l => getLineType(l) === 'warning').length;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm" onClick={onClose} />

      {/* Modal: Fullscreen on mobile, centered + max-w on desktop */}
      <div className="fixed inset-0 z-[61] flex items-end sm:items-center justify-center sm:p-4">
        <div
          className="w-full sm:max-w-4xl bg-gray-950 border border-gray-800 shadow-2xl flex flex-col overflow-hidden
                     rounded-t-2xl sm:rounded-xl
                     h-[92vh] sm:h-[85vh]"
        >

          {/* ── Header Row 1: Title + Close ───────────────────────────── */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-gray-800 bg-gray-900">
            <div className="flex items-center gap-2 min-w-0">
              <Terminal className="h-4 w-4 text-green-400 flex-shrink-0" />
              <span className="font-semibold text-white text-sm">System Logs</span>
              <span className="hidden sm:inline text-xs text-gray-500 truncate">
                allflowx-backend · letzte 300 Zeilen
              </span>
            </div>

            {/* Action buttons + Close in one row */}
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
              {/* Copy – icon only on mobile, icon+text on desktop */}
              <button
                onClick={copyLogs}
                disabled={loading || lines.length === 0}
                data-testid="logs-copy-btn"
                title="Logs kopieren"
                className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs transition-colors disabled:opacity-50 active:scale-95"
              >
                {copied
                  ? <Check className="h-4 w-4 text-green-400" />
                  : <Copy className="h-4 w-4" />
                }
                <span className="hidden sm:inline">{copied ? 'Kopiert!' : 'Kopieren'}</span>
              </button>

              {/* Refresh – icon only on mobile */}
              <button
                onClick={fetchLogs}
                disabled={loading}
                data-testid="logs-refresh-btn"
                title="Aktualisieren"
                className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs transition-colors disabled:opacity-50 active:scale-95"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Aktualisieren</span>
              </button>

              {/* Close */}
              <button
                onClick={onClose}
                data-testid="logs-close-btn"
                className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 transition-colors active:scale-95"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* ── Subtitle (mobile-only second row) ───────────────────────── */}
          <div className="sm:hidden flex items-center justify-between px-4 py-1.5 bg-gray-900 border-b border-gray-800">
            <span className="text-xs text-gray-500">allflowx-backend · letzte 300 Zeilen</span>
            {lastRefresh && (
              <span className="text-xs text-gray-600">{lastRefresh}</span>
            )}
          </div>

          {/* ── Last refresh (desktop only, inside header) ───────────────── */}
          {lastRefresh && (
            <div className="hidden sm:flex items-center px-4 py-1 bg-gray-900 border-b border-gray-800">
              <span className="text-xs text-gray-600">Aktualisiert: {lastRefresh}</span>
            </div>
          )}

          {/* ── Filter Bar ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-800 bg-gray-900/50 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {[
              { key: 'all', label: `Alle (${lines.length})`, color: '' },
              { key: 'errors', label: `Fehler (${errorCount})`, color: errorCount > 0 ? 'text-red-400' : '' },
              { key: 'warnings', label: `Warn. (${warnCount})`, color: warnCount > 0 ? 'text-yellow-400' : '' },
            ].map(({ key, label, color }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`logs-filter-${key}`}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filter === key
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : `bg-gray-800 text-gray-400 hover:bg-gray-700 border border-transparent ${color}`
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── Log Output ──────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto p-2 sm:p-3 font-mono text-xs leading-relaxed">
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-500 gap-2">
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>Logs werden geladen...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                Keine Einträge für diesen Filter.
              </div>
            ) : (
              filtered.map((line, i) => {
                const type = getLineType(line);
                return (
                  <div
                    key={i}
                    className={`flex gap-1.5 py-0.5 px-1.5 sm:px-2 rounded ${
                      type === 'error' ? 'bg-red-950/30' :
                      type === 'warning' ? 'bg-yellow-950/20' : ''
                    }`}
                  >
                    <LineIcon type={type} />
                    {/* break-all ensures long URLs/tokens wrap on mobile */}
                    <span
                      className={`break-all whitespace-pre-wrap leading-5 ${
                        type === 'error' ? 'text-red-400' :
                        type === 'warning' ? 'text-yellow-400' :
                        'text-gray-300'
                      }`}
                    >
                      {line}
                    </span>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* ── Status Bar ──────────────────────────────────────────────── */}
          <div className="px-4 py-2 border-t border-gray-800 bg-gray-900 flex items-center gap-4 text-xs">
            <span className="text-red-400">{errorCount} Fehler</span>
            <span className="text-yellow-400">{warnCount} Warnungen</span>
            <span className="text-gray-500">{lines.length - errorCount - warnCount} Info</span>
          </div>

        </div>
      </div>
    </>
  );
};
